import React, { useState, useEffect, useRef } from "react";

function get(obj, path, fallback) {
  var result = obj;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    if (result === null || result === undefined) return fallback;
    result = result[parts[i]];
  }
  return result === undefined || result === null ? fallback : result;
}

function cleanHtmlTags(val) {
  if (typeof val === "string") {
    return val.replace(/<\/?p>/gi, " ").trim();
  }
  if (Array.isArray(val)) {
    return val.map(item => cleanHtmlTags(item));
  }
  if (val !== null && typeof val === "object") {
    const cleaned = {};
    for (let k in val) cleaned[k] = cleanHtmlTags(val[k]);
    return cleaned;
  }
  return val;
}

function getInitialValue(val) {
  if (val === null || val === undefined) return "";
  return cleanHtmlTags(val);
}

function setDeep(obj, path, newValue) {
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let current = root;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
    current = current[key];
  }
  current[path[path.length - 1]] = newValue;
  return root;
}

const AutoResizeTextarea = ({ value, onChange, readOnly, style, placeholder }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const extraRowHeight = 18; 
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + extraRowHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      style={{
        ...style,
        resize: "none",
        overflow: "hidden",
        boxSizing: "border-box",
        fontFamily: "inherit",
        lineHeight: "1.4"
      }}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      rows={1} 
    />
  );
};

function AmazonProductListingEditableField({ field = {}, onFieldChange, changedValue, isUpdated }) {
  var rawValue = isUpdated ? changedValue : get(field, "listing.value", null);
  var initial = getInitialValue(rawValue);

  var [value, setValue] = useState(initial);

  var options = get(field, "schema.allowed_values", []) || [];
  
  var isDropdown = get(field, "ui.input_type", "") === "dropdown" || options.length > 0;
  var isEditable = get(field, "schema.editable", true);

  var examples = get(field, "schema.examples", []) || [];
  var rules = get(field, "schema.rules", {}) || {};

 var fieldType = rules.type;

var actualFieldType = fieldType;

var maxLength =
  actualFieldType === "string"
    ? (
        rules.maxLength ||
        rules.maxUtf8ByteLength
      )
    : null;
  var minLength = rules.minLength;
  var minimum = rules.minimum;
  var maximum = rules.maximum;

  var isNumberField =
  (
    actualFieldType === "number" ||
    actualFieldType === "integer"
  ) &&
  !Array.isArray(value) &&
  typeof value !== "object";
  var placeholder =
  actualFieldType === "boolean"
    ? "Select"
    : (
        examples.length > 0
          ? `e.g. ${examples[0]}`
          : ""
      );
      
  var currentLength =
    typeof value === "string"
      ? value.length
      : 0;

  var isTooLong =
  maxLength &&
  currentLength > maxLength;
  var numericValue = Number(value);

var isNumberInvalid =
  isNumberField &&
  value !== "" &&
  (
    (minimum !== undefined &&
      numericValue < minimum) ||

    (maximum !== undefined &&
      numericValue > maximum)
  );
  var isTooShort =
    minLength &&
    currentLength < minLength &&
    currentLength > 0;  

  var inputStyle = {
    width: "100%", 
    fontSize: 12, 
    padding: "4px 8px", 
    borderRadius: 6,
    border:
isTooLong ||
isTooShort ||
isNumberInvalid
      ? "1px solid red"
      : "1px solid #ccc", 
    background: "var(--color-background-primary, #ffffff)", 
    color: "#333", 
    cursor: "text" 
  };

  var plainTextStyle = {
    width: "100%",
    fontSize: 12,
    padding: "4px 0px", 
    color: "#555",
    wordBreak: "break-word"
  };

  const getDisplayText = (val) => {
    if (val === null || val === undefined) return "—";
    const strVal = String(val).trim();
    return strVal === "" ? "—" : strVal;
  };

  const handleStandardChange = (newVal) => {
    if (!isEditable) return;
    setValue(newVal);
    if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
  };

  const handleDeepChange = (path, newVal) => {
    if (!isEditable) return;
    const updatedObject = setDeep(value, path, newVal);
    setValue(updatedObject);
    if (onFieldChange) onFieldChange({ field: field.field, value: updatedObject });
  };

  useEffect(() => {
    setValue(isUpdated ? changedValue : getInitialValue(get(field, "listing.value", null)));
  }, [field, changedValue, isUpdated]);

  if (isDropdown) {
    let selectedValue = "";
    if (value !== null && value !== undefined) {
      if (Array.isArray(value) && value.length > 0) {
        selectedValue = String(value[0]);
      } else if (typeof value !== "object") {
        selectedValue = String(value);
      }
    }

    if (!isEditable) {
      return <div style={plainTextStyle}>{getDisplayText(selectedValue)}</div>;
    }

    return (
      <select
        style={inputStyle}
        value={selectedValue}
        onChange={(e) => {
          if (!isEditable) return;
          const originalIsArray = Array.isArray(get(field, "listing.value", null));
          const newVal = originalIsArray ? [e.target.value] : e.target.value;
          
          setValue(newVal);
          if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
        }}
      >
        <option value="">
          {placeholder || "Select"}
        </option>

        {options.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  const renderDeep = (data, path = [], isObjProp = false) => {
    if (data === null || data === undefined) {

      if (!isEditable) {
        return (
          <div
            style={{
              ...plainTextStyle,
              width: isObjProp ? "60%" : "100%"
            }}
          >
            —
          </div>
        );
      }
    
      if (isNumberField) {
    
        return (
          <div style={{ width: isObjProp ? "60%" : "100%" }}>
    
            <AutoResizeTextarea
              style={{
                ...inputStyle,
                width: "100%",
                marginBottom: 0
              }}
              value=""
              onChange={(e) =>
                handleDeepChange(
                  path,
                  e.target.value
                )
              }
              placeholder={placeholder}
            />
    
            <div className="mt-1">
    
              <small className="text-muted">
    
                Allowed range:
    
                {" "}
    
                {minimum !== undefined &&
 maximum !== undefined && (
  <>
    {minimum} - {maximum}
  </>
)}

{minimum !== undefined &&
 maximum === undefined && (
  <>
    Min: {minimum}
  </>
)}

{minimum === undefined &&
 maximum !== undefined && (
  <>
    Max: {maximum}
  </>
)}
    
              </small>
    
            </div>
    
          </div>
        );
      }
    
      return null;
    }
    if (typeof data !== "object") {
     
      if (!isEditable) {
        return (
          <div
            style={{
              ...plainTextStyle,
              width: isObjProp ? "60%" : "100%"
            }}
          >
            {getDisplayText(data)}
          </div>
        );
      }
    
      const textValue =
        data !== null && data !== undefined
          ? String(data)
          : "";
          const localIsNumberField =
          (
            actualFieldType === "number" ||
            actualFieldType === "integer"
          ) &&
          !isNaN(Number(textValue)) &&
          textValue !== "";
          const currentLength =
          textValue.length;
    
      const isTooLong =
        maxLength &&
        currentLength > maxLength;
        const numericValue = Number(textValue);

        const isNumberInvalid =
        localIsNumberField &&
          textValue !== "" &&
          (
            (minimum !== undefined &&
              numericValue < minimum) ||
        
            (maximum !== undefined &&
              numericValue > maximum)
          );
      const isTooShort =
        minLength &&
        currentLength < minLength &&
        currentLength > 0;
    
      return (
        <div style={{ width: isObjProp ? "60%" : "100%" }}>
    
    <AutoResizeTextarea
  style={{
    ...inputStyle,
    width: "100%",
    marginBottom: 0,
    border:
  isTooLong ||
  isTooShort ||
  isNumberInvalid
        ? "1px solid red"
        : "1px solid #ccc"
  }}
  value={textValue}
  onChange={(e) => {

    let newValue = e.target.value;

    // allow only numbers
    if (localIsNumberField) {

      if (
        newValue !== "" &&
        !/^\d*\.?\d*$/.test(newValue)
      ) {
        return;
      }
    }

    handleDeepChange(
      path,
      newValue
    );
  }}
  placeholder={placeholder}
/>
    
    {!localIsNumberField &&
  (maxLength || minLength) && (
            
            <div className="d-flex justify-content-between mt-1">
    
              <div>
    
                {isTooLong && (
                  <small className="text-danger">
                    Maximum {maxLength} characters allowed
                  </small>
                )}
    
               
    
              </div>
    
              <small
                className={
                  isTooLong
                    ? "text-danger"
                    : "text-muted"
                }
              >
                {currentLength}
                {maxLength
                  ? ` / ${maxLength}`
                  : ""}
              </small>
    
            </div>
          )}
 
        </div>
      );
    }

    if (Array.isArray(data)) {
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {data.map((item, index) => {
            const isPrimitive = typeof item !== "object" || item === null;
            return (
              <div 
                key={index} 
                style={{ 
                  marginBottom: isPrimitive ? "4px" : (index < data.length - 1 ? "8px" : "0")
                }}
              >
                {renderDeep(item, [...path, index])}
              </div>
            );
          })}
        </div>
      );
    }

    if (typeof data === "object") {
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {Object.entries(data).map(([k, v]) => {
            const currentPath = [...path, k];
            
            if (typeof v !== "object" || v === null) {
              return (
                <div key={k} style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "500", width: "35%", paddingRight: "5px", color: "#555", wordBreak: "break-word" }}>
                    {k}:
                  </span>
                  {renderDeep(v, currentPath, true)}
                </div>
              );
            }
            
            return (
              <div key={k} style={{ marginBottom: "6px", backgroundColor: "#fdfdfd", padding: "6px", borderRadius: "4px", border: "1px solid #ebebeb" }}>
                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#333", marginBottom: "4px", textTransform: "capitalize" }}>
                  {k.replace(/_/g, " ")}
                </div>
                <div style={{ paddingLeft: "4px" }}>
                  {renderDeep(v, currentPath)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  if (value !== null && typeof value === "object") {
    return renderDeep(value);
  }

  if (!isEditable) {
    return (
      <div style={plainTextStyle}>
        {getDisplayText(value)}
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
  
  <AutoResizeTextarea
  style={inputStyle}
  value={
    value !== null && value !== undefined
      ? String(value)
      : ""
  }
  onChange={(e) => {

    let newValue = e.target.value;

    // allow only numbers
    if (isNumberField) {

      if (
        newValue !== "" &&
        !/^\d*\.?\d*$/.test(newValue)
      ) {
        return;
      }
    }

    handleStandardChange(newValue);
  }}
  placeholder={placeholder}
/>
  
  {!isNumberField &&
  (maxLength || minLength) && (
        <div
          className="d-flex justify-content-between mt-1"
        >
  
          <div>
  
            {isTooLong && (
              <small className="text-danger">
                Maximum {maxLength} characters allowed
              </small>
            )}
  
          
          </div>
  
          <small
            className={
              isTooLong
                ? "text-danger"
                : "text-muted"
            }
          >
            {currentLength}
            {maxLength
              ? ` / ${maxLength}`
              : ""}
          </small>
  
        </div>
      )}
 {isNumberField &&
 minimum !== undefined &&
 (
   actualFieldType === "number" ||
   actualFieldType === "integer"
 ) && (
  <div className="d-flex justify-content-end mt-1">

    <small className="text-muted">

      {minimum !== undefined &&
       maximum !== undefined && (
        <>
          {minimum} - {maximum}
        </>
      )}

      {minimum !== undefined &&
       maximum === undefined && (
        <>
          Min: {minimum}
        </>
      )}

      {minimum === undefined &&
       maximum !== undefined && (
        <>
          Max: {maximum}
        </>
      )}

    </small>

  </div>
)}
{isNumberInvalid && (
  <div className="d-flex justify-content-end mt-1">

    <small className="text-danger">

      Value must be between{" "}

      {minimum !== undefined &&
 maximum !== undefined && (
  <>
    {minimum} - {maximum}
  </>
)}

{minimum !== undefined &&
 maximum === undefined && (
  <>
    minimum {minimum}
  </>
)}

{minimum === undefined &&
 maximum !== undefined && (
  <>
    maximum {maximum}
  </>
)}

    </small>

  </div>
)}
    </div>
  );
}

export default AmazonProductListingEditableField;